'use client';

import { ValidationError } from '@eliadmin/core/src/api/errors';
import { sendContactMessage } from '@eliadmin/core/src/api/forms/forms-api';
import { uploadSecretFile } from '@eliadmin/core/src/api/general/general-api';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Textarea } from '@eliadmin/core/src/components/ui/textarea';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { CONTACT_CONFIG } from '@eliadmin/core/src/constants/contact-config';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { trackFormSubmitSuccess } from '@eliadmin/core/src/components/analytics/analytics-helpers';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';

import { useForm } from 'react-hook-form';
import { FiSend } from 'react-icons/fi';
import { MdOutlineFileUpload } from 'react-icons/md';
import { useRef, useState, type ChangeEvent } from 'react';
import { z } from 'zod';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { GDPRTextContactForm } from '@eliadmin/core/src/utils/gdpr-tos/gdpr-text-contact-form';
import { Field } from '../form/labeled-field';
import { Paragraph } from '../ui/paragraph';
import Link from '@eliadmin/core/src/components/link';
import { getDefaultUrlForTosOrGdprLocalized } from '@eliadmin/core/src/utils/misc-utils';
const contactSchema = (VM: FormValidationMessages) =>
  z.object({
    name: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    surname: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
    text: z.string(),
    gdpr: z.boolean().refine(val => val === true, { message: VM.GDPR_VALIDATION_MESSAGE() }),
    attachments: z.array(z.object({ id: z.number() })).optional(),
    'g-recaptcha-response': z.string().default(''),
  });

export type ContactFormValues = z.infer<ReturnType<typeof contactSchema>>;

export function ContactForm() {
  const { t } = useTranslations();
  const templateSettings = useGlobalStore(s => s.templateSettings);
  const showAttachments = !!templateSettings?.contact?.contact_form_attachments?.enabled;
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const MAX_FILE_SIZE = 2 * 1024 * 1024;
  const MAX_FILES = 15;

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema(formValidationMessages)),
    defaultValues: {
      name: '',
      surname: '',
      email: '',
      text: '',
      gdpr: false,
      attachments: [],
    },
  });

  const { mutateAsync } = useMutation({
    mutationFn: sendContactMessage,
  });

  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();

  const handlePickFiles = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    setFileError(null);

    if (selectedFiles.length === 0) {
      setFiles([]);
      return;
    }

    if (selectedFiles.length > MAX_FILES) {
      setFileError(`${t('text3457')} ${MAX_FILES}`);
      setFiles([]);
      return;
    }

    const oversizedFiles = selectedFiles.filter(file => file.size > MAX_FILE_SIZE);
    if (oversizedFiles.length > 0) {
      setFileError(t('text3458'));
      setFiles([]);
      return;
    }

    setFiles(selectedFiles);
  };

  const handleSubmit = form.handleSubmit(async data => {
    try {
      if (showAttachments && files.length > 0) {
        if (files.length > MAX_FILES) {
          setFileError(`${t('text3457')} ${MAX_FILES}`);
          return;
        }

        const attachments: { id: number }[] = [];

        for (const file of files) {
          if (file.size > MAX_FILE_SIZE) {
            setFileError(t('text3458'));
            return;
          }

          const formData = new FormData();
          formData.append('file', file);

          try {
            const res = await uploadSecretFile({
              formData,
              locale,
            });

            if (res.data?.id) {
              attachments.push({ id: res.data.id });
            }
          } catch {
            setFileError(t('text3459'));
            return;
          }
        }

        data.attachments = attachments;
      }

      await mutateAsync({
        data: {
          ...data,

        },
        locale,
      });
      trackFormSubmitSuccess({
        form_id: 'contact',
        form_name: 'contact_form',
        form_destination: typeof window !== 'undefined' ? window.location.href : undefined,
        form_length: Object.keys(data).length,
      });
      toast({
        title: t('text658'),
        variant: 'success',
      });
      router.push(ROUTES.HOME());
    } catch (err) {
      // handle 422
      if (err instanceof ValidationError) {
        for (const [field, errors] of Object.entries(err.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text657'),
        description: (err as any)?.message,
        variant: 'destructive',
      });
    }
  });

  const GDPRhref = getDefaultUrlForTosOrGdprLocalized(ROUTES?.GDPR?.() ?? '/', locale, 'gdpr');

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit}
        className={cn('flex w-full flex-col items-start justify-start gap-4', 'ea-contact-form')}
      >
        <div className="grid w-full grid-cols-12 gap-4">
          <Field
            control={form.control}
            name="name"
            label={t('text3179')}
            placeholder={t('text3179')}
            className={{ wrapper: 'col-span-12 w-full' }}
            required
          />
          <Field
            control={form.control}
            name="email"
            type="email"
            label="E-mail"
            placeholder="E-mail"
            className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
            required
          />
          <Field
            control={form.control}
            name="phone"
            label={t('text3559')}
            placeholder={t('text3559')}
            className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
            required
          />
          <Field
            control={form.control}
            name="text"
            type="textarea"
            label={t('text2340')}
            placeholder={t('text2340')}
            className={{ wrapper: 'col-span-12 w-full' }}
            required
          />
          <FormField
            control={form.control}
            name="gdpr"
            render={({ field }) => (
              <FormItem
                className={cn(
                  'col-span-12 flex items-start',
                  'ea-contact-form__gdpr-checkbox-item ea-contact-form__checkbox-item',
                )}
              >
                <FormControl>
                  <Checkbox
                    ref={field.ref}
                    name={field.name}
                    disabled={field.disabled}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    className={cn('', 'ea-contact-form__gdpr-checkbox ea-contact-form__checkbox')}
                  />
                </FormControl>
                <FormLabel className={cn('', 'ea-contact-form__gdpr-checkbox-label')}>
                  <Paragraph className="text-sm font-light" color="black">
                    {t('text613')}&nbsp;
                    <Link
                      prefetch={false}
                      target="_blank"
                      href={GDPRhref}
                      className="font-bold text-primary"
                    >
                      {t('text614')}
                    </Link>
                    &nbsp;{t('text3354')}
                  </Paragraph>
                </FormLabel>
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name={'g-recaptcha-response'}
          render={() => (
            <FormItem required className={cn('col-span-12')}>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button
          type="submit"
          variant={CONTACT_CONFIG.form.submit.variant}
          className={cn('col-span-12 w-full sm:w-fit', 'ea-contact-form__submit')}
          isLoading={form.formState.isSubmitting}
        >
          <FiSend className="mr-2 h-5 w-5" />
          {t('text185')}
        </Button>
      </form>
    </Form>
  );
}
